import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import Layout from '../components/Layout';
import {api} from '../api';

export default function SelfMapping() {
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [governmentId, setGovernmentId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [user, setUser] = useState(null);
  const [checkingMapping, setCheckingMapping] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already mapped before showing the form
    api('/me').then(r => r.ok ? r.json() : null).then(user => {
      const displayName = document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith('displayName='));
      setUser({ ...user, displayName: displayName ? decodeURIComponent(displayName.split('=')[1]) : null });
      if (user && user.userId) {
        // Check if user is already mapped to an employee
        api('/hcm/employees?q=' + encodeURIComponent(user.userId)).then(r => r.ok ? r.json() : []).then(employees => {
          const userEmployee = employees.find(emp => emp.userId === user.userId);
          if (userEmployee) {
            // User is already mapped, redirect to dashboard
            navigate('/');
            return;
          }
          setCheckingMapping(false);
        }).catch(() => {
          setCheckingMapping(false);
        });
      } else {
        setCheckingMapping(false);
      }
    }).catch(() => {
      setCheckingMapping(false);
    });
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api('/hcm/employees/self-map', {
        method: 'POST',
        body: JSON.stringify({
          dateOfBirth,
          governmentId: governmentId.replace(/[\s\-.]/g, '')
        })
      });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          window.location.href = '/';
        }, 2000);
      } else {
        const errorData = await response.text();
        setError('Unable to match your information with an employee record. Please verify your date of birth and government ID, or contact your administrator.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    }

    setLoading(false);
  };

  if (checkingMapping) {
    return (
      <Layout>
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Checking account status...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (success) {
    return (
      <Layout>
        <div className="max-w-md mx-auto">
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
            <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-green-900 mb-2">Successfully Mapped!</h2>
            <p className="text-green-700">Your account has been linked to your employee record. Redirecting...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-lg shadow p-8">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Link Your Account</h1>
            <p className="text-gray-600 mt-2">
              To access timeclock features, please verify your identity by providing your date of birth and a government ID number.
            </p>
          </div>

          {user && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Account:</strong> {user.displayName || user.username || 'Current User'}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date of Birth
              </label>
              <input
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Government ID Number
              </label>
              <input
                type="text"
                value={governmentId}
                onChange={(e) => setGovernmentId(e.target.value)}
                placeholder="Enter your SSN, Driver's License, etc."
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter any government ID number from your employee record (SSN, Driver's License, Passport, etc.)
              </p>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-2 px-4 rounded-md text-white font-medium ${
                loading 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500'
              }`}
            >
              {loading ? 'Verifying...' : 'Link Account'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Can't find your information? Contact your administrator for assistance.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}